import { APPConfig, getLocalStorage, IS_BROWSER } from 'config/appConfig'
import { getLocaleCodeFromUrl } from 'src/utils/localeUtils'
import { storeContainer } from 'src/models/Store'
import { getResourceFrom3rdPartyUrl } from 'src/api/localeResources'
import { getConfiguration } from '@nuskin/configuration-sdk'

const EQUINOX_STOREFRONT_CONFIG_MAP = 'equinox_storefront'

function getCountryFromLocale(locale = '') {
  const localeParts = locale.replace('-', '_').split('_')
  return (localeParts[1] || localeParts[0] || '').toUpperCase()
}

function getContentstackEnvironment(config = {}, activeEnv = '') {
  const envSource = [
    config?.cs_cloud_domain,
    config?.mysiteRedesignApiDomain,
    config?.domain,
    activeEnv,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  if (envSource.includes('stage') || envSource.includes('stg')) {
    return 'stage'
  }

  if (envSource.includes('test') || envSource.includes('tst')) {
    return 'test'
  }

  if (envSource.includes('dev') || envSource.includes('localhost')) {
    return 'dev'
  }

  return 'prod'
}

async function getEquinoxStorefrontConfig({
  country,
  environment,
  clientId,
} = {}) {
  if (!country || !environment || !clientId) {
    return {}
  }

  try {
    const configMaps = await getConfiguration([EQUINOX_STOREFRONT_CONFIG_MAP], {
      country,
      environment,
      clientId,
    })
    return configMaps?.[EQUINOX_STOREFRONT_CONFIG_MAP] || {}
  } catch (e) {
    console.log('error while loading equinox_storefront config map', e)
    return {}
  }
}

function setAppConfigByStore(options) {
  return new Promise((resolve, reject) => {
    try {
      const {
        pageUrl,
        localeProp = 'defaultLocale',
        cbk,
        host,
        hostName,
      } = options

      const appConfigFromJSON = APPConfig.getConfigFromJSON()
      //const hostName = new URL(pageUrl).host
      /**
       * @info
       *  when the request comes like IP address
       */
      const activeEnv = APPConfig.getLocalEnv()
      // const localDomainNames = appConfigFromJSON?.localEnvs
      // const isLocalEnv = localDomainNames.some(domainName =>
      //   hostName.includes(domainName)
      // )

      // const activeEnv = isLocalEnv ? APPConfig.getLocalEnv() : hostName
      // //const activeEnv = hostName.includes('local') ? localActiveEnv : hostName

      const currentLocaleInLocalStorage = getLocalStorage(localeProp) || ''
      const defaultLocale =
        currentLocaleInLocalStorage ||
        appConfigFromJSON.defaultProps.defaultLocale
      const curLocaleFromUrl = getLocaleCodeFromUrl({
        url: pageUrl,
        at: 'pathParam',
        defaultLocale: defaultLocale,
        isReverseType: true,
      })

      let defaultConfigs =
        appConfigFromJSON.domainsConfig[host] ||
        appConfigFromJSON.domainsConfig[hostName] ||
        appConfigFromJSON.domainsConfig[activeEnv]

      // console.log({
      //   curLocaleFromUrl,
      //   localeFromCookie: getCookie(localeProp),
      //   localeFromLS: getLocalStorage(localeProp),
      // })

      // setting the global config replaces the older config
      // and the comparison is failing in getGlobalStoreResponse
      const mergedAppConfig = {
        ...appConfigFromJSON.defaultProps,
        ...defaultConfigs,
        defaultLocale: curLocaleFromUrl,
      }

      APPConfig.setAppConfig(mergedAppConfig, true, curLocaleFromUrl)
      const activeAppConfig = APPConfig.getActiveAppConfig(curLocaleFromUrl)

      storeContainer.getStoreListByLocale(
        mergedAppConfig.globalStoreId,
        pageUrl,
        defaultLocale,
        async (
          activeStoreResponse,
          globalStoreResponse,
          newProps,
          storeConfig
        ) => {
          try {
            if (!IS_BROWSER && storeConfig?.activeLocale !== curLocaleFromUrl) {
              storeConfig = {
                ...storeConfig,
                activeStoreResponse: { status: 'success' },
              }
            }
            storeContainer.storeConfigAtGlobal(storeConfig, curLocaleFromUrl)
            let storefrontConfigFromGlobalStoreAdmin = {}
            let activeStoreCommonConfigs = {}
            let storefrontConfigFromActiveStoreAdmin = {}
            try {
              let storefrontConfig =
                globalStoreResponse?.properties?.find(
                  property => property?.name == 'storefrontConfig'
                ) || ''
              let cspDomains =
                globalStoreResponse?.properties?.find(
                  property => property?.name == 'cspDomains'
                ) || ''
              if (storefrontConfig) {
                storefrontConfigFromGlobalStoreAdmin = JSON.parse(
                  storefrontConfig?.value || {}
                )
                if (cspDomains) {
                  storefrontConfigFromGlobalStoreAdmin['serverProperties'][
                    'cspDomains'
                  ] = JSON.parse(cspDomains.value || {})
                }
              }
            } catch (e) {
              console.log(
                'error while parsing storefrontConfigFromGlobalStoreAdmin ',
                e
              )
            }
            try {
              const ignoreStoreConfig = [
                'activateB2B2C',
                'discountModelRewardTypes',
                'tierConfigMap',
                'PriceRuleConfigMap',
                'allowedRewardTypesForOptIn',
                'saleEventStartTime',
                'saleEventEndTime',
                'storefrontConfig',
              ]

              try {
                activeStoreResponse?.properties?.forEach(property => {
                  if (!ignoreStoreConfig?.includes(property?.name)) {
                    try {
                      activeStoreCommonConfigs[property.name] = JSON.parse(
                        property.value || {}
                      )
                    } catch (e) {
                      activeStoreCommonConfigs[property.name] = property.value
                    }
                  }
                })
              } catch (e) {
                console.error('Fn setAppConfigByStore ', e)
              }

              let storefrontConfig =
                activeStoreResponse?.properties?.find(
                  property => property?.name == 'storefrontConfig'
                ) || ''

              if (storefrontConfig) {
                storefrontConfigFromActiveStoreAdmin = JSON.parse(
                  storefrontConfig?.value || {}
                )
              }
            } catch (e) {
              console.log(
                'error while parsing storefrontConfigFromActiveStoreAdmin'
              )
            }
            const commonProperties = {}
            const ignoreGlobalStoreProperties = [
              'activateB2B2C',
              'PriceRuleConfigMap',
              'storefrontConfig',
              'cspDomains',
              'allStoreIds',
            ]

            globalStoreResponse?.properties?.forEach(property => {
              if (!ignoreGlobalStoreProperties?.includes(property?.name)) {
                commonProperties[property.name] = property.value
              }
            })
            const finalConfig = {
              ...mergedAppConfig,
              ...newProps,
              ...storefrontConfigFromGlobalStoreAdmin,
              ...storefrontConfigFromActiveStoreAdmin,
              ...activeStoreCommonConfigs,
              ...commonProperties,
              defaultLocale: curLocaleFromUrl,
              pageUrl,
            }

            const isDevProxyEnabled = process.env.ENABLE_DEV_PROXY || false
            if (isDevProxyEnabled) {
              finalConfig.apiDomainName = mergedAppConfig.apiDomainName
              finalConfig.orchestrationPathPrefix =
                mergedAppConfig.orchestrationPathPrefix
            }

            const equinoxStorefrontConfig = await getEquinoxStorefrontConfig({
              country: getCountryFromLocale(curLocaleFromUrl),
              environment: getContentstackEnvironment(finalConfig, activeEnv),
              clientId: finalConfig?.client_id,
            })

            if (Object.keys(equinoxStorefrontConfig)?.length > 0) {
              finalConfig.equinoxStorefrontConfig = {
                ...(finalConfig.equinoxStorefrontConfig || {}),
                ...equinoxStorefrontConfig,
              }

              if (equinoxStorefrontConfig.useSmartyAddressForm !== undefined) {
                finalConfig.useSmartyAddressForm =
                  equinoxStorefrontConfig.useSmartyAddressForm
              }
            }

            try {
              const loadParams = {
                fileName: 'redirectUrls',
                locale: curLocaleFromUrl.replace('_', '-'),
                domainName:
                  finalConfig.localeResourceFilesDomain || 'dev.nuskin.com',
              }

              let redirectUrls = await getResourceFrom3rdPartyUrl(loadParams)
              if (redirectUrls.statusCode === 200) {
                delete redirectUrls.responseStatus
                delete redirectUrls.status
                delete redirectUrls.statusCode
                delete redirectUrls.totalCount
                finalConfig.redirectUrls = redirectUrls
              } else {
                finalConfig.redirectUrls = {}
              }
            } catch (e) {
              console.log('error in receiving redirectionUrls', e)
            }
            APPConfig.updateAppConfig(finalConfig, true, curLocaleFromUrl)
            await cbk?.(storeConfig)
            resolve(storeConfig)
          } catch (error) {
            reject(error)
          }
        },
        options
      )
    } catch (error) {
      reject(error)
    }
  })
}

export { setAppConfigByStore }
